import {
  Plus,
  Edit,
  Trash2,
  ChevronLeft,
  ChevronRight,
  Users as UsersIcon,
  UserCheck,
  Mail,
} from "lucide-react";
import { useState, useEffect } from "react";
import usersService, {
  UserListParams,
  UserMetricsResponse,
  type Users,
} from "../../services/users.service";
import ConfirmationDialog from "../../widgets/ConfirmationModal";
import UserFormModal from "../../components/users/UserForm";
import toast from "react-hot-toast";
import Loader from "../../widgets/Loader";
import Tooltip from "../../widgets/ToolTip";
import { SearchFilter, MetricCard } from "./ui";
import Can from "../../components/auth/Can";

function Users() {
  const [users, setUsers] = useState<Users[]>([]);
  const [isLoadingUsers, setIsLoadingUsers] = useState(true);
  const [totalCount, setTotalCount] = useState(0);
  const [metrics, setMetrics] = useState<UserMetricsResponse | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [searchFilters, setSearchFilters] = useState({
    userName: "",
    email: "",
  });

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editUserId, setEditUserId] = useState<string | undefined>();

  // Fetch users (paged)
  const fetchUsers = async () => {
    try {
      setIsLoadingUsers(true);
      const params: UserListParams = {
        skip: (currentPage - 1) * pageSize,
        limit: pageSize,
        ...(searchFilters.userName && { search_text: searchFilters.userName }),
      };
      const response = await usersService.getUsers(params);
      setUsers(response?.data?.users || []);
      setTotalCount(response?.data?.totalUsers || 0);
    } catch (error: any) {
      const msg =
        error.response?.data?.error?.error_description ||
        error.response?.data?.message ||
        "Failed to fetch users";
      toast.error(msg);
    } finally {
      setIsLoadingUsers(false);
    }
  };

  // Fetch tenant-wide metrics for the cards (separate, doesn't change with paging)
  const fetchMetrics = async () => {
    try {
      const data = await usersService.getTotalCounts();
      setMetrics(data || null);
    } catch (error) {
      console.error("Error fetching user metrics:", error);
    }
  };

  useEffect(() => {
    fetchUsers();
  }, [currentPage, searchFilters, pageSize]);

  useEffect(() => {
    fetchMetrics();
  }, []);

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const handleSearchChange = (field: string, value: string) => {
    setSearchFilters((prev: any) => ({ ...prev, [field]: value }));
    setCurrentPage(1);
  };

  // Delete
  const [deleteUser, setDeleteUser] = useState<Users | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isDialogOpen, setIsDialogOpen] = useState(false);

  const handleConfirmDelete = async () => {
    if (!deleteUser) return;
    try {
      setIsDeleting(true);
      await usersService.deleteUser(deleteUser.user_id);
      fetchUsers();
      fetchMetrics();
      toast.success("User deleted successfully");
      setIsDialogOpen(false);
      setDeleteUser(null);
    } catch (error: any) {
      const msg =
        error?.response?.data?.error?.error_description ||
        error?.response?.data?.message ||
        "Failed to delete user";
      toast.error(msg);
    } finally {
      setIsDeleting(false);
    }
  };

  // Modal
  const handleCreateUser = () => {
    setEditUserId(undefined);
    setIsModalOpen(true);
  };
  const handleEditUser = (userId: string) => {
    setEditUserId(userId);
    setIsModalOpen(true);
  };
  const handleCloseModal = () => {
    setIsModalOpen(false);
    setEditUserId(undefined);
  };
  const handleRefresh = () => {
    setIsModalOpen(false);
    setEditUserId(undefined);
    fetchUsers();
    fetchMetrics();
  };

  // Pagination math
  const totalPages = Math.ceil(totalCount / pageSize);
  const startItem = totalCount === 0 ? 0 : (currentPage - 1) * pageSize + 1;
  const endItem = Math.min(currentPage * pageSize, totalCount);

  // Status pill colors
  const statusPillClass = (status?: string) => {
    switch ((status || "").toLowerCase()) {
      case "active":
        return "bg-emerald-50 text-emerald-700";
      case "pending":
        return "bg-amber-50 text-amber-700";
      default:
        return "bg-gray-100 text-gray-600";
    }
  };
  const statusDotClass = (status?: string) => {
    switch ((status || "").toLowerCase()) {
      case "active":
        return "bg-emerald-500";
      case "pending":
        return "bg-amber-500";
      default:
        return "bg-gray-400";
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="sticky top-0 z-40 bg-white border-slate-200 border-b mb-4">
        <div className="px-6 py-4">
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
            <div>
              <h1 className="text-xl font-bold text-gray-900">Users</h1>
              <p className="text-xs text-slate-600">
                Manage user accounts and permissions
              </p>
            </div>
            <div className="flex items-center justify-end gap-2">
              <SearchFilter
                placeholder="Search by username..."
                value={searchFilters.userName}
                onChange={(v) => handleSearchChange("userName", v)}
                className="w-64"
                debounceMs={400}
              />
              <Can permission="users:create">
              <button
                className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm font-medium"
                onClick={handleCreateUser}
                type="button"
              >
                <Plus className="w-4 h-4" />
                Add User
              </button>
              </Can>
            </div>
          </div>
        </div>
      </div>

      {/* Metric Cards */}
      <div className="mb-4">
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <MetricCard
            icon={<UsersIcon className="w-5 h-5" />}
            label="Total Users"
            value={metrics?.user?.total_count ?? 0}
            accent="blue"
            isLoading={!metrics}
          />
          <MetricCard
            icon={<UserCheck className="w-5 h-5" />}
            label="Active Users"
            value={metrics?.user?.total_active ?? 0}
            sub={
              metrics
                ? `${Math.round(
                    metrics.user.total_count
                      ? (metrics.user.total_active / metrics.user.total_count) *
                          100
                      : 0,
                  )}% of all users`
                : undefined
            }
            accent="emerald"
            isLoading={!metrics}
          />
          <MetricCard
            icon={<Mail className="w-5 h-5" />}
            label="Pending Invites"
            value={metrics?.user_invite?.total_pending ?? 0}
            accent="amber"
            isLoading={!metrics}
          />
        </div>
      </div>

      {/* Users Table */}
      <div className="pb-6">
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden shadow-sm">
          <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center">
            <h3 className="text-base font-semibold text-gray-900">
              Users List
            </h3>
            <div className="text-sm text-gray-500">
              {totalCount > 0 &&
                `Showing ${startItem}-${endItem} of ${totalCount} users`}
            </div>
          </div>

          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                    User
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                    Email
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                    Groups
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-semibold text-gray-600 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-100">
                {isLoadingUsers ? (
                  <tr>
                    <td
                      colSpan={5}
                      className="px-6 py-12 text-center text-gray-500"
                    >
                      <Loader />
                    </td>
                  </tr>
                ) : users?.length === 0 ? (
                  <tr>
                    <td
                      colSpan={5}
                      className="px-6 py-12 text-center text-gray-500"
                    >
                      No users found.
                    </td>
                  </tr>
                ) : (
                  users.map((user: any) => {
                    const fullName =
                      [user?.name?.givenName, user?.name?.familyName]
                        .filter(Boolean)
                        .join(" ") ||
                      user?.userName ||
                      "—";
                    const email = user?.emails?.[0]?.value || "—";

                    // Group names assigned to this user; dedupe.
                    const groupNames: string[] = Array.from(
                      new Set(
                        (user?.groups || [])
                          .map((g: any) => g?.group_name)
                          .filter(Boolean),
                      ),
                    );

                    return (
                      <tr
                        key={user?._id}
                        className="hover:bg-gray-50 transition-colors"
                      >
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center gap-3">
                            <div className="w-9 h-9 rounded-full bg-blue-50 text-blue-600 flex items-center justify-center font-semibold text-sm">
                              {fullName.charAt(0).toUpperCase()}
                            </div>
                            <div>
                              <div className="text-sm font-medium text-gray-900">
                                {fullName}
                              </div>
                              {user?.userName && (
                                <code className="text-[11px] px-1.5 py-0.5 bg-gray-100 text-gray-700 rounded">
                                  {user.userName}
                                </code>
                              )}
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">
                          {email}
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-700 align-top max-w-[360px] whitespace-normal break-words">
                          {groupNames.length === 0 ? (
                            <span className="text-gray-400">—</span>
                          ) : (
                            groupNames.join(", ")
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span
                            className={`inline-flex items-center gap-1.5 px-2 py-1 text-xs font-medium rounded-full ${statusPillClass(user?.status)}`}
                          >
                            <span
                              className={`w-1.5 h-1.5 rounded-full ${statusDotClass(user?.status)}`}
                            />
                            {user?.status || "unknown"}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                          <div className="flex items-center justify-end gap-1">
                            <Can permission="users:edit">
                            <Tooltip content="Edit">
                              <button
                                className="text-blue-600 hover:bg-blue-50 p-2 rounded-lg transition-colors"
                                type="button"
                                onClick={() => handleEditUser(user?.user_id)}
                              >
                                <Edit className="w-4 h-4" />
                              </button>
                            </Tooltip>
                            </Can>
                            <Can permission="users:delete">
                            <Tooltip content="Delete">
                              <button
                                className="text-red-600 hover:bg-red-50 p-2 rounded-lg transition-colors"
                                type="button"
                                onClick={() => {
                                  setDeleteUser(user);
                                  setIsDialogOpen(true);
                                }}
                              >
                                <Trash2 className="w-4 h-4" />
                              </button>
                            </Tooltip>
                            </Can>
                          </div>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination Footer */}
          {totalCount > 0 && (
            <div className="px-6 py-4 border-t border-gray-200 flex flex-col sm:flex-row items-center justify-between gap-4">
              <div className="flex items-center gap-4">
                <div className="text-sm text-gray-700">
                  Showing <span className="font-medium">{startItem}</span> to{" "}
                  <span className="font-medium">{endItem}</span> of{" "}
                  <span className="font-medium">{totalCount}</span> results
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-sm text-gray-600">Show:</span>
                  <select
                    value={pageSize}
                    onChange={(e) => {
                      setPageSize(Number(e.target.value));
                      setCurrentPage(1);
                    }}
                    className="px-2 py-1 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value={10}>10</option>
                    <option value={20}>20</option>
                    <option value={50}>50</option>
                  </select>
                </div>
              </div>

              {totalPages > 1 && (
                <div className="flex items-center space-x-1">
                  <button
                    onClick={() => handlePageChange(currentPage - 1)}
                    disabled={currentPage === 1}
                    type="button"
                    className="inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <ChevronLeft className="w-4 h-4" />
                  </button>

                  {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                    let pageNum;
                    if (totalPages <= 5) pageNum = i + 1;
                    else if (currentPage <= 3) pageNum = i + 1;
                    else if (currentPage >= totalPages - 2)
                      pageNum = totalPages - 4 + i;
                    else pageNum = currentPage - 2 + i;

                    return (
                      <button
                        key={pageNum}
                        onClick={() => handlePageChange(pageNum)}
                        type="button"
                        className={`inline-flex items-center px-4 py-2 border text-sm font-medium ${
                          currentPage === pageNum
                            ? "z-10 bg-blue-50 border-blue-500 text-blue-600"
                            : "bg-white border-gray-300 text-gray-500 hover:bg-gray-50"
                        }`}
                      >
                        {pageNum}
                      </button>
                    );
                  })}

                  <button
                    onClick={() => handlePageChange(currentPage + 1)}
                    disabled={currentPage === totalPages}
                    type="button"
                    className="inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <ChevronRight className="w-4 h-4" />
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* User Form Modal */}
      <UserFormModal
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        onRefresh={handleRefresh}
        userId={editUserId}
      />

      {/* Delete Confirmation Dialog */}
      <ConfirmationDialog
        isOpen={isDialogOpen}
        onClose={() => setIsDialogOpen(false)}
        onConfirm={handleConfirmDelete}
        iconName="delete"
        title="Delete User"
        description={
          deleteUser
            ? `Are you sure you want to delete <strong>${deleteUser?.name?.givenName} ${deleteUser?.name?.familyName}</strong>? This action cannot be undone.`
            : "Are you sure you want to delete this user?"
        }
        confirmLabel={isDeleting ? "Deleting..." : "Delete"}
        cancelLabel="Cancel"
      />
    </div>
  );
}

export default Users;
